/**
 * /claim-name — the D115 `name_pending` picker.
 *
 * A state that should last under a minute, and that App.tsx routes *every*
 * path to while it lasts: the email is proven, the name is not, and there is
 * nothing else this person can usefully do until they pick one. No cancel, no
 * dismiss, no bottom nav — a "later" button here would leave someone verified
 * and nameless, appearing to the room as "(choosing a name)" indefinitely.
 *
 * The endpoint is `POST /api/auth/claim-name`, deliberately not `PATCH /api/me`:
 * no name was ever held, so the once-per-24h rename cooldown and the
 * `name_history` write both have the wrong semantics here.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useStore } from '../lib/store'
import { ApiFailure, GET, POST } from '../lib/api'
import { Notice, Screen, Spacer } from '../ui'

const DEBOUNCE_MS = 400

interface NameAvailability {
  available: boolean
  reason?: string
  message?: string
  suggestions?: string[]
}

type NameStatus =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'ok' }
  | { kind: 'taken'; message: string; suggestions: string[] }

export default function ClaimName() {
  const { state, refresh } = useStore()

  const [name, setName] = useState('')
  const [status, setStatus] = useState<NameStatus>({ kind: 'idle' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inflight = useRef<AbortController | null>(null)

  const check = useCallback(async (candidate: string) => {
    inflight.current?.abort()
    const ac = new AbortController()
    inflight.current = ac
    setStatus({ kind: 'checking' })
    try {
      const res = await GET<NameAvailability>(
        `/api/auth/name-available?name=${encodeURIComponent(candidate)}`, ac.signal,
      )
      if (ac.signal.aborted) return
      if (res.available) setStatus({ kind: 'ok' })
      else setStatus({
        kind: 'taken',
        message: res.message ?? 'That one’s taken too.',
        suggestions: (res.suggestions ?? []).slice(0, 3),
      })
    } catch {
      if (!ac.signal.aborted) setStatus({ kind: 'idle' })
    }
  }, [])

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    const trimmed = name.trim()
    if (trimmed.length < 2) {
      inflight.current?.abort()
      setStatus({ kind: 'idle' })
      return
    }
    timer.current = setTimeout(() => { void check(trimmed) }, DEBOUNCE_MS)
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [name, check])

  useEffect(() => () => { inflight.current?.abort() }, [])

  const claim = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await POST('/api/auth/claim-name', { displayName: name.trim() })
      await refresh()
    } catch (e) {
      if (e instanceof ApiFailure && (e.code === 'name_taken' || e.code === 'name_held')) {
        setStatus({ kind: 'taken', message: e.message, suggestions: [] })
      } else {
        setError(e instanceof ApiFailure ? e.message : 'That didn’t go through. Try again.')
      }
      setBusy(false)
    }
  }, [name, refresh])

  const canClaim = name.trim().length >= 2 && status.kind !== 'taken' && !busy

  // The name that was lost is the one the room now shows to someone else; the
  // placeholder on our own record is never worth showing back (D115).
  const lostName = state?.user.displayName && !/^Guest \d+$/.test(state.user.displayName)
    ? state.user.displayName
    : null

  return (
    <Screen style={{ justifyContent: 'center', padding: '0 24px' }}>
      <main style={{ width: '100%', maxWidth: 440, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
          {lostName ? <>Someone else claimed “{lostName}”</> : <>That name got taken</>}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--dim)', margin: '12px 0 0', lineHeight: 1.55 }}>
          Your email is verified, but while you were away, that name got taken. Pick a name and
          it’s yours immediately.
        </p>

        {error ? <><Spacer h={16} /><Notice tone="alert">{error}</Notice></> : null}

        <Spacer h={20} />
        <label htmlFor="claim-name" style={{ fontSize: 12, color: 'var(--dim)' }}>New name</label>
        <input
          id="claim-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && canClaim) void claim() }}
          maxLength={24}
          autoFocus
          autoComplete="nickname"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          aria-describedby="claim-name-status"
          placeholder="Pick something"
          style={{
            width: '100%', marginTop: 6, padding: '15px 16px', borderRadius: 14, minHeight: 48,
            border: `1px solid ${status.kind === 'taken' ? 'var(--alert-line)' : 'var(--line)'}`,
            background: 'var(--surf)', fontSize: 15, color: 'var(--fg)',
          }}
        />

        <div id="claim-name-status" aria-live="polite" style={{ minHeight: 20, marginTop: 8 }}>
          {status.kind === 'ok' ? (
            <span style={{ fontSize: 12, color: 'var(--bright)' }}>✓ Available</span>
          ) : status.kind === 'checking' ? (
            <span style={{ fontSize: 12, color: 'var(--dim)' }}>Checking…</span>
          ) : status.kind === 'taken' ? (
            <span style={{ fontSize: 12, color: 'var(--alert-fg)' }}>{status.message}</span>
          ) : null}
        </div>

        {status.kind === 'taken' && status.suggestions.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {status.suggestions.map((s) => (
              <button
                key={s}
                onClick={() => setName(s)}
                className="mono"
                style={{
                  fontSize: 11, fontWeight: 600, padding: '8px 14px', borderRadius: 999,
                  minHeight: 44, border: '1px solid var(--line)', color: 'var(--bright)',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        ) : null}

        <Spacer h={20} />
        <button
          onClick={() => { void claim() }}
          disabled={!canClaim}
          style={{
            display: 'block', width: '100%', textAlign: 'center', padding: 15, minHeight: 48,
            borderRadius: 14, fontSize: 15, fontWeight: 700,
            background: canClaim ? 'var(--fill)' : 'var(--line)',
            color: canClaim ? 'var(--ink)' : 'var(--dim)',
          }}
        >
          {busy ? 'Claiming…' : 'Claim it'}
        </button>

        <p style={{ fontSize: 12, color: 'var(--dim)', marginTop: 18, lineHeight: 1.6 }}>
          This one is permanent the moment you tap it. Your email is already verified, so the name
          binds to it immediately.
        </p>
      </main>
    </Screen>
  )
}
